import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { VStack } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useContactUsHandler, useIsChatAppEnabled } from '../../../../../hooks';
import type { AccountTabScreenId } from '../../../../../navigation/AppNavigation.props';
import { accountMenuStaticLinkConfig } from '../../../../../screens/AccountScreen/AccountScreen.navigation';
import { AccountMenuLink } from '../AccountMenuLink';
import { AccountMenuPrimaryItem } from '../AccountMenuPrimaryItem';

export const AccountPrimaryMenu = (props: AccountPrimaryMenuProps) => {
  const { screenId, ...stackProps } = props;

  const { formatMessage } = useIntl();
  const { handleContactUs } = useContactUsHandler('account');

  // ─── Flags ───────────────────────────────────────────────────────────

  const isChatAppEnabled = useIsChatAppEnabled();
  const isNotificationPreferencesEnabled = useFeatureFlag('CELS-3674-enable-notification-preference-center'); // prettier-ignore
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore

  return (
    <VStack {...stackProps}>
      <AccountMenuLink
        testID="account.sidebar-tab.profile"
        label={formatMessage(messages.accountInfo)}
        iconName="IconProfileStroke"
        to={accountMenuStaticLinkConfig.profileLinkConfig}
        active={screenId === 'profile'}
      />

      <AccountMenuLink
        label={formatMessage(messages.payments)}
        iconName="IconCreditCard"
        to={accountMenuStaticLinkConfig.paymentAndGiftCardsLinkConfig}
        active={screenId === 'payment-and-gift-cards'}
      />

      <AccountMenuLink
        label={formatMessage(messages.creditAndPromoCodes)}
        iconName="IconBadgeScan"
        to={accountMenuStaticLinkConfig.creditAndPromoCodesLinkConfig}
        active={screenId === 'credit-and-promo-codes'}
      />

      {isInStoreOrdersEnabled ? (
        <AccountMenuLink
          testID="account.sidebar-tab.gift-cards"
          label={formatMessage(messages.giftCards)}
          iconName="IconPromo"
          to={accountMenuStaticLinkConfig.giftCardsLinkConfig}
          active={screenId === 'gift-cards'}
        />
      ) : null}

      <AccountMenuLink
        testID="account.sidebar-tab.addresses"
        label={formatMessage(messages.addresses)}
        iconName="IconPickupStrokeSm"
        to={accountMenuStaticLinkConfig.addressesLinkConfig}
        active={screenId === 'addresses'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.orders"
        label={formatMessage(messages.orders)}
        iconName="IconBowl"
        to={accountMenuStaticLinkConfig.ordersLinkConfig}
        active={screenId === 'orders'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.favorites"
        label={formatMessage(messages.favorites)}
        iconName="IconHeartStroke"
        to={accountMenuStaticLinkConfig.favoritesLinkConfig}
        active={screenId === 'favorites'}
      />

      <AccountMenuLink
        testID="account.sidebar-tab.dietary-restrictions"
        label={formatMessage(messages.dietaryRestrictions)}
        iconName="IconFilter"
        to={accountMenuStaticLinkConfig.dietaryRestrictionsLinkConfig}
        active={screenId === 'dietary-restrictions'}
      />

      {isNotificationPreferencesEnabled ? (
        <AccountMenuLink
          label={formatMessage(messages.notifications)}
          iconName="IconBell"
          to={accountMenuStaticLinkConfig.notifications}
          active={screenId === 'notifications'}
        />
      ) : null}

      {isChatAppEnabled ? (
        <AccountMenuPrimaryItem
          testID="account.sidebar-tab.contact-us"
          label={formatMessage(messages.contactUs)}
          iconName="IconText"
          onPress={handleContactUs}
        />
      ) : null}
    </VStack>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type AccountPrimaryMenuProps = Readonly<{
  screenId?: AccountTabScreenId | null;
}> &
  Omit<React.ComponentProps<typeof VStack>, 'children'>;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  accountInfo: {
    defaultMessage: 'Account info',
    description: 'Account | Menu | Account info',
  },
  creditAndPromoCodes: {
    defaultMessage: 'Credit & promo codes',
    description: 'Account | Menu | Credit & promo codes',
  },
  payments: {
    defaultMessage: 'Payments',
    description: 'Account | Menu | Payments',
  },
  addresses: {
    defaultMessage: 'Addresses',
    description: 'Account | Menu | Addresses',
  },
  giftCards: {
    defaultMessage: 'Gift cards',
    description: 'Account | Menu | Gift cards',
  },
  orders: {
    defaultMessage: 'Orders',
    description: 'Account | Menu | Orders',
  },
  favorites: {
    defaultMessage: 'Favorites',
    description: 'Account | Menu | Favorites',
  },
  dietaryRestrictions: {
    defaultMessage: 'Dietary restrictions',
    description: 'Account | Menu | Dietary restrictions',
  },
  notifications: {
    defaultMessage: 'Notifications',
    description: 'Account | Menu | Notifications',
  },
  contactUs: {
    defaultMessage: 'Contact us',
    description: 'Account | Menu | Contact us',
  },
});
